/**
 * 売上・原価の簡易入力(暫定)。
 * 経費と違い承認フローを通さず、登録した時点で実績(確定)になる。
 * 運送はフェーズ3でRoutine自動連携に置き換わる前提。
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import { todayStr, toMonthLabel, toShortDate } from '@core/lib/date';
import { formatAmountInput, formatYen, parseAmount } from '@core/lib/money';
import type { DepartmentId, HierarchyNode, Transaction } from '@core/lib/types';
import {
  createDirectEntry,
  deleteDirectEntry,
  fetchDirectEntries,
  isDirectEntryAccount,
  updateDirectEntry,
  type DirectEntryInput,
} from './revenueRepo';
import { fetchHierarchyNodes, toPathLabel } from '@core/masters/hierarchyRepo';
import { fetchVendors } from '@core/masters/vendorRepo';
import SalesImportModal from '@core/receivables/SalesImportModal';
import FixedRevenueModal from '@core/receivables/FixedRevenueModal';
import { isPayable, isReceivable } from '@core/partners/terms';
import type { Vendor } from '@core/lib/types';

type FormState = {
  editingId: string | null;
  companyId: string;
  departmentId: DepartmentId;
  accountId: string;
  subAccountId: string;
  hierarchyNodeId: string;
  postingDate: string;
  amountText: string;
  counterparty: string;
  description: string;
  /** 取引先マスタの取引先(2026-10-23)。空 = 選ばない(取引先の欄に自由に書く) */
  vendorId: string;
};

export default function RevenueEntryPage() {
  const { user, isAdmin, canConfirm, flowRole } = useAuth();
  const {
    departments,
    accounts,
    subAccountsOf,
    accountName,
    subAccountName,
    fiscal,
    companies,
    defaultCompanyId,
  } = useMasters();
  const { month } = usePeriod();
  const toast = useToast();

  const myDepartments = departments.filter((d) => user?.department_ids.includes(d.id));
  const defaultDept = (user?.primary_department_id ?? myDepartments[0]?.id ?? 'honbu') as DepartmentId;
  const directAccounts = accounts.filter((a) => a.active && isDirectEntryAccount(a));

  const [form, setForm] = useState<FormState>({
    editingId: null,
    companyId: defaultCompanyId,
    departmentId: defaultDept,
    accountId: '',
    subAccountId: '',
    hierarchyNodeId: '',
    postingDate: todayStr(),
    amountText: '',
    counterparty: '',
    description: '',
    vendorId: '',
  });
  const [vendors, setVendors] = useState<Vendor[]>([]);
  useEffect(() => {
    fetchVendors()
      .then(setVendors)
      .catch(() => setVendors([]));
  }, []);
  const [nodes, setNodes] = useState<HierarchyNode[]>([]);
  const [rows, setRows] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // 台帳(シート)からの一括取り込み(2026-10-23 段階3)
  const [importOpen, setImportOpen] = useState(false);
  // 毎月の定額売上(2026-10-23 段階3)。計上前に全取引の有無を確かめるため、経理・管理者だけ
  const [fixedOpen, setFixedOpen] = useState(false);
  const canPostFixed = isAdmin || canConfirm || flowRole === 'accounting';

  const load = useCallback(async () => {
    if (accounts.length === 0) return;
    setLoading(true);
    try {
      setRows(await fetchDirectEntries(form.departmentId, month, accounts));
    } catch (e) {
      toast(e instanceof Error ? e.message : '一覧を取得できませんでした');
    } finally {
      setLoading(false);
    }
    // 階層マスタは未登録でも入力を止めない(取得できなければ選択肢を空にするだけ)
    try {
      setNodes(await fetchHierarchyNodes(form.departmentId));
    } catch {
      setNodes([]);
    }
  }, [accounts, form.departmentId, month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function resetForm() {
    setForm((f) => ({
      ...f,
      editingId: null,
      accountId: '',
      subAccountId: '',
      hierarchyNodeId: '',
      postingDate: todayStr(),
      amountText: '',
      counterparty: '',
      description: '',
      vendorId: '',
    }));
  }

  async function save() {
    if (!user) return;
    const amount = parseAmount(form.amountText);
    if (amount === null) {
      toast('金額を正しく入力してください(1円以上の整数)');
      return;
    }
    const input: DirectEntryInput = {
      companyId: form.companyId || defaultCompanyId,
      departmentId: form.departmentId,
      accountId: form.accountId,
      subAccountId: form.subAccountId,
      hierarchyNodeId: form.hierarchyNodeId || null,
      postingDate: form.postingDate,
      amount,
      counterparty: form.vendorId ? (vendors.find((v) => v.id === form.vendorId)?.name ?? form.counterparty) : form.counterparty,
      description: form.description,
      vendorId: form.vendorId || null,
    };

    setBusy(true);
    try {
      if (form.editingId) {
        await updateDirectEntry({
          id: form.editingId,
          input,
          accounts,
          userId: user.id,
          fiscalStartMonth: fiscal.start_month,
        });
        toast('修正しました');
      } else {
        await createDirectEntry({
          input,
          accounts,
          userId: user.id,
          fiscalStartMonth: fiscal.start_month,
        });
        toast('登録しました。実績に計上されます');
      }
      resetForm();
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function remove(tx: Transaction) {
    if (!user) return;
    setBusy(true);
    try {
      await deleteDirectEntry(tx.id, user.id);
      toast('削除しました');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '削除に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const subAccounts = form.accountId ? subAccountsOf(form.accountId) : [];

  return (
    <section className="screen">
      <div className="card">
        <h3>{form.editingId ? '売上・原価を修正する' : '売上・原価を入力する(暫定)'}</h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          経費と違い、承認フローを通さず<b>登録した時点で実績(確定)になります</b>。
          運送はフェーズ3でRoutine連携による自動取込に置き換わります。
        </p>
        <div className="form-grid">
          {companies.length > 1 && (
            <div>
              <label>会社</label>
              <select
                value={form.companyId}
                onChange={(e) => setForm({ ...form, companyId: e.target.value })}
              >
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label>事業部</label>
            <select
              value={form.departmentId}
              onChange={(e) =>
                setForm({ ...form, departmentId: e.target.value as DepartmentId, hierarchyNodeId: '' })
              }
            >
              {myDepartments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>科目①(売上・原価のみ)</label>
            <select
              value={form.accountId}
              onChange={(e) => setForm({ ...form, accountId: e.target.value, subAccountId: '' })}
            >
              <option value="">選択してください</option>
              {directAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}({a.pl_line === 'revenue' ? '売上' : '原価'})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>科目②</label>
            <select
              value={form.subAccountId}
              disabled={!form.accountId}
              onChange={(e) => setForm({ ...form, subAccountId: e.target.value })}
            >
              <option value="">選択してください</option>
              {subAccounts.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>階層(任意)</label>
            <select
              value={form.hierarchyNodeId}
              onChange={(e) => setForm({ ...form, hierarchyNodeId: e.target.value })}
              disabled={nodes.length === 0}
            >
              <option value="">{nodes.length === 0 ? '(階層マスタ未登録)' : '指定しない'}</option>
              {nodes.map((n) => (
                <option key={n.id} value={n.id}>
                  {toPathLabel(n, nodes)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>
              計上日 <span style={{ fontWeight: 400 }}>— PLに反映</span>
            </label>
            <input
              type="date"
              value={form.postingDate}
              onChange={(e) => setForm({ ...form, postingDate: e.target.value })}
            />
          </div>
          <div>
            <label>金額(円)</label>
            <input
              inputMode="numeric"
              placeholder="1,200,000"
              value={form.amountText}
              onChange={(e) => setForm({ ...form, amountText: formatAmountInput(e.target.value) })}
            />
          </div>
          <div>
            <label>取引先(任意)</label>
            <select value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })}>
              <option value="">取引先マスタから選ばない</option>
              {vendors
                .filter((v) => {
                  const pl = accounts.find((a) => a.id === form.accountId)?.pl_line;
                  return pl === 'cogs' ? isPayable(v) : isReceivable(v);
                })
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}({v.code})
                  </option>
                ))}
            </select>
          </div>
          {!form.vendorId && (
            <div>
              <label>取引先の名前(任意・マスタに無いとき)</label>
              <input
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
              />
            </div>
          )}
          <div className="fw">
            <label>摘要(任意)</label>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="例)紀伊国屋 8月分 配送売上"
            />
          </div>
        </div>

        <button className="btn-green" onClick={() => void save()} disabled={busy}>
          {busy ? '送信中…' : form.editingId ? '修正する' : '登録する'}
        </button>
        {form.editingId && (
          <button className="btn-line" style={{ marginLeft: 10 }} onClick={resetForm} disabled={busy}>
            キャンセル
          </button>
        )}
      </div>

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          入力済みの売上・原価({toMonthLabel(month)})
          <span className="hint" style={{ fontWeight: 400 }}>
            — 上の「事業部」で選んだ事業部の分を表示しています
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
            {canPostFixed && (
              <button className="btn-line" onClick={() => setFixedOpen(true)}>
                毎月の定額売上
              </button>
            )}
            <button className="btn-line" onClick={() => setImportOpen(true)}>
              一括取り込み
            </button>
          </span>
        </h3>
        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : rows.length === 0 ? (
          <p className="empty">この月の入力はまだありません。</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>計上日</th>
                <th>科目①</th>
                <th>科目②</th>
                <th>取引先</th>
                <th>摘要</th>
                <th className="num">金額</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((tx) => (
                <tr key={tx.id}>
                  <td>{toShortDate(tx.posting_date)}</td>
                  <td>{accountName(tx.account_id)}</td>
                  <td>{subAccountName(tx.sub_account_id)}</td>
                  <td>{tx.counterparty ?? ''}</td>
                  <td>{tx.description ?? ''}</td>
                  <td className="num">{formatYen(tx.amount)}</td>
                  {isAdmin && (
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="txn-link"
                        onClick={() =>
                          setForm({
                            editingId: tx.id,
                            companyId: tx.company_id ?? defaultCompanyId,
                            departmentId: tx.department_id,
                            accountId: tx.account_id,
                            subAccountId: tx.sub_account_id,
                            hierarchyNodeId: tx.hierarchy_node_id ?? '',
                            postingDate: tx.posting_date,
                            amountText: formatAmountInput(String(tx.amount)),
                            counterparty: tx.counterparty ?? '',
                            description: tx.description ?? '',
                            vendorId: tx.vendor_id ?? '',
                          })
                        }
                      >
                        修正
                      </button>
                      <button className="txn-link" onClick={() => void remove(tx)} disabled={busy}>
                        削除
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="hint" style={{ marginTop: 10 }}>
          修正・削除ができるのは管理者のみです(暫定入力の誤りを直せるようにしています)。
          経費は確定後は誰も変更できません。
        </p>
      </div>
      {importOpen && <SalesImportModal onClose={() => setImportOpen(false)} onDone={() => void load()} />}
      {fixedOpen && <FixedRevenueModal month={month} onClose={() => setFixedOpen(false)} onDone={() => void load()} />}
    </section>
  );
}
